import api from './api';

/**
 * ativarEmissaoService (Story 1-7 / T1.29 — AC9/AC10).
 *
 * Camada de serviço da tela wizard "Ativar emissão". Concentra as 4 chamadas ao
 * backend do Delivery (que por sua vez faz proxy à atac-notas):
 *   - getCompany    → GET   /empresa/:id            (estado fiscal consolidado)
 *   - patchFiscal   → PATCH /empresa/:id/fiscal     (habilita + provisiona/ambiente)
 *   - postCertificado → POST /empresa/:id/certificado (multipart .pfx + senha)
 *   - postCsc       → POST  /empresa/:id/csc         (csc/idCsc/ambiente)
 *
 * Usa o axios central `services/api` (Bearer token via defaults do auth hook),
 * mesmo padrão da aba Fiscal (Story 1-4). A senha do certificado só transita no
 * FormData; nunca é guardada nem logada aqui.
 */

export interface EmpresaFiscal {
  id: number;
  /** CNPJ do emitente (cadastro da empresa) — exibição apenas. */
  cnpj_cpf?: string | null;
  inscricao_estadual?: string | null;
  endereco?: {
    logradouro?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
  } | null;
  codigo_municipio_ibge?: string | null;
  regime_tributario_id?: number | null;
  ambiente_fiscal?: string | null;
  emissao_habilitada?: boolean;
  certificado_status?: string | null;
  certificado_validade?: string | null;
  csc_status?: string | null;
  atac_notas_tenant_id?: string | null;
}

export interface PatchFiscalPayload {
  emissao_habilitada: boolean;
  ambiente_fiscal: number | string;
}

export interface CscPayload {
  csc: string;
  idCsc: string;
  ambiente: number;
}

/**
 * Campos fiscais obrigatórios (Story 1-4) que o passo 1 do wizard valida antes
 * de provisionar. Retorna a lista de rótulos faltantes (vazia = ok).
 */
export function getMissingFiscalFields(
  company: EmpresaFiscal | null | undefined,
): string[] {
  if (!company) return ['Dados da empresa'];

  const missing: string[] = [];
  const endereco = company.endereco || {};

  if (!company.inscricao_estadual) missing.push('Inscrição Estadual');
  if (!company.codigo_municipio_ibge) missing.push('Código do município (IBGE)');
  if (!company.regime_tributario_id) missing.push('Regime tributário');
  if (!endereco.logradouro) missing.push('Endereço (logradouro)');
  if (!endereco.cidade) missing.push('Endereço (município)');
  if (!endereco.estado) missing.push('Endereço (UF)');

  return missing;
}

async function getCompany(id: number | string): Promise<EmpresaFiscal> {
  const { data } = await api.get(`/empresa/${id}`);
  return data;
}

async function patchFiscal(
  id: number | string,
  payload: PatchFiscalPayload,
): Promise<EmpresaFiscal> {
  const { data } = await api.patch(`/empresa/${id}/fiscal`, payload);
  return data;
}

async function postCertificado(
  id: number | string,
  file: File,
  password: string,
): Promise<EmpresaFiscal> {
  const form = new FormData();
  // Campo "certificado" casa com upload.single("certificado") no backend.
  form.append('certificado', file);
  form.append('password', password);

  // Não fixamos Content-Type: o browser/axios define multipart/form-data com a
  // boundary automaticamente ao receber um FormData.
  const { data } = await api.post(`/empresa/${id}/certificado`, form);
  return data;
}

async function postCsc(
  id: number | string,
  payload: CscPayload,
): Promise<EmpresaFiscal> {
  const { data } = await api.post(`/empresa/${id}/csc`, payload);
  return data;
}

export default {
  getCompany,
  patchFiscal,
  postCertificado,
  postCsc,
};
